// REALISTIC CAMEL (2026-11-04, owner: realistic models, commercial use, "my
// game should be unique"). A rigged, animated dromedary — "Camel" by
// E.A.Cornell, CC-BY 4.0 — reworked by agent/animals-asset/camel-pipeline.mjs
// (neutral tintable hide, our own teal/sand/magenta blanket weave, masked hair
// cards, two clips). See public/models/camel/CREDITS.md and assetCredits.ts.
//
// Same contract as horseModel.ts (VehicleMesh owns the lifecycle):
// - Armed ONLY by warmCamelModel() under the match load screen, after one real
//   frame through the game's render path compiled its programs (colour +
//   skinned shadow depth, opaque + alpha-masked hair) and uploaded its
//   textures. Not loaded by then → the round keeps the procedural camel; it
//   never pops in mid-fight.
// - Geometry/textures are shared (cached GLB); the HIDE + HAIR materials are
//   cloned per camel so each carries its own tint (the vehicle paint / rider
//   colour) — a uniform, so every tint hits the same compiled program.
// - No per-frame allocation: update() writes weights/timeScales on
//   preallocated actions and calls mixer.update once.
import * as THREE from "three";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { instantiate, loadGltf } from "./gltfAssets.js";

export const CAMEL_GLB_URL = "/models/camel/camel.glb";

/** Fit target: the procedural camel's head/ears stand at ~3.1 m with the hump
 *  saddle at ~2.75 m (worldEntities.ts). The model's bind pose carries its head
 *  high, so its full height is matched to 3.0 m (→ ~3.7 m nose to tail, the
 *  procedural camel's length). */
const TARGET_HEIGHT = 3.0;

const IDLE = 0, WALK = 1;
/** Ground speed the in-place walk reads right at (m/s, after the fit):
 *  measured off the clip — a planted hoof slides ~0.86 model-units/s
 *  (agent/animals-asset/stride.mjs on the source walk), × the fit scale (~1.55). */
const WALK_NOMINAL = 1.35;
/** The source has no run: past a brisk walk the same cycle is played faster
 *  (a camel's pace IS a long-legged quick walk), capped so the legs never
 *  churn; beyond the cap the hooves skate a little at full speed. */
const WALK_MAX_RATE = 2.6;
/** Hysteretic gait band (m/s). */
const WALK_ON = 0.45, WALK_OFF = 0.22;
/** Hide/hair materials (tinted per instance). */
const TINTED = new Set(["Camel", "CamelHair"]);

let template: GLTF | null = null;
let fitYaw = 0, fitScale = 1;
const fitOffset = new THREE.Vector3();
/** Saddle anchor: the top of the blanketed hump in template (bind-pose) space. */
const humpTop = new THREE.Vector3();
/** OUR TACK (owner: the model must be clearly ours, not just recoloured): a
 *  riding saddle with front/rear posts and brass knobs, two canvas saddle bags
 *  with leather flaps and magenta tassels, a strap across the hump. Built once
 *  in metres (vehicle space), merged into ONE vertex-coloured geometry → one
 *  extra draw call per camel; rides the back joint so it sways with the gait. */
let tackGeo: THREE.BufferGeometry | null = null;
let tackMat: THREE.MeshStandardMaterial | null = null;
const COAT_LIFT = new THREE.Color("#efe3cc");
let armed = false;
let armedFor: object | null = null;

export function camelModelArmed(): boolean { return armed && template !== null; }
export function disarmCamelModel(): void { armed = false; armedFor = null; }

function prepare(g: GLTF | null): GLTF | null {
  if (!g || template) return template ?? g;
  g.scene.updateMatrixWorld(true);
  // precise bounds at bind pose (Box3 skins the vertices) — once, ~20k verts
  const box = new THREE.Box3().setFromObject(g.scene, true);
  const size = box.getSize(new THREE.Vector3());
  if (!(size.y > 0) || !Number.isFinite(size.y)) { console.error("[camel] unexpected GLB bounds"); return null; }
  // the LONG horizontal axis is nose→tail; the head end is the one whose top
  // reaches higher (the head is carried above the hump) — sample the front /
  // back 20 % of that axis
  const alongX = size.x > size.z;
  const lo = alongX ? box.min.x : box.min.z, hi = alongX ? box.max.x : box.max.z;
  let topLo = -Infinity, topHi = -Infinity;
  const v = new THREE.Vector3();
  let tinted = 0;
  g.scene.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    const pos = m.geometry.getAttribute("position");
    for (let i = 0; i < pos.count; i += 3) {
      m.getVertexPosition(i, v);
      v.applyMatrix4(m.matrixWorld);
      const a = alongX ? v.x : v.z;
      if (a < lo + (hi - lo) * 0.2) topLo = Math.max(topLo, v.y);
      else if (a > hi - (hi - lo) * 0.2) topHi = Math.max(topHi, v.y);
    }
    // bounding sphere once at bind pose, padded for the stride's reach
    m.computeBoundingSphere();
    if (m.boundingSphere) m.boundingSphere.radius *= 1.3;
    m.castShadow = true;
    m.receiveShadow = true;
    if (TINTED.has((m.material as THREE.Material).name)) tinted++;
  });
  if (tinted < 2) { console.error("[camel] hide/hair materials not found"); return null; }
  for (const name of ["idle", "walk"]) {
    if (!THREE.AnimationClip.findByName(g.animations, name)) { console.error(`[camel] clip ${name} missing`); return null; }
  }
  // hump top: the highest skinned vertex in the middle 30 % of the length —
  // hide + blanket only: the hair CARDS are mostly-transparent quads that
  // stand far above the visible fur (the first cut seated the saddle on them)
  humpTop.set(0, -Infinity, 0);
  g.scene.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh || (m.material as THREE.Material).name === "CamelHair") return;
    const pos = m.geometry.getAttribute("position");
    for (let i = 0; i < pos.count; i += 2) {
      m.getVertexPosition(i, v);
      v.applyMatrix4(m.matrixWorld);
      const a = alongX ? v.x : v.z;
      if (a > lo + (hi - lo) * 0.35 && a < hi - (hi - lo) * 0.35 && v.y > humpTop.y) humpTop.copy(v);
    }
  });
  if (alongX) humpTop.z = (box.min.z + box.max.z) / 2; else humpTop.x = (box.min.x + box.max.x) / 2;
  const headAtHi = topHi > topLo;
  // rotate so the head end points to −Z (vehicle forward)
  if (alongX) fitYaw = headAtHi ? Math.PI / 2 : -Math.PI / 2;
  else fitYaw = headAtHi ? Math.PI : 0;
  fitScale = TARGET_HEIGHT / size.y;
  const c = box.getCenter(new THREE.Vector3());
  fitOffset.set(-c.x, -box.min.y, -c.z);
  template = g;
  console.log(`[camel] model ready: ${size.x.toFixed(2)}×${size.y.toFixed(2)}×${size.z.toFixed(2)} → scale ${fitScale.toFixed(3)}, yaw ${fitYaw.toFixed(2)}`);
  return g;
}

function buildTack(): void {
  if (tackGeo) return;
  const parts: THREE.BufferGeometry[] = [];
  const col = new THREE.Color();
  const add = (geo: THREE.BufferGeometry, hex: string, x: number, y: number, z: number, rx = 0, rz = 0): void => {
    const g = geo.index ? geo.toNonIndexed() : geo;
    if (g !== geo) geo.dispose();
    g.deleteAttribute("uv");
    g.rotateX(rx); g.rotateZ(rz); g.translate(x, y, z);
    col.set(hex);
    const n = g.getAttribute("position").count, c = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { c[i * 3] = col.r; c[i * 3 + 1] = col.g; c[i * 3 + 2] = col.b; }
    g.setAttribute("color", new THREE.BufferAttribute(c, 3));
    parts.push(g);
  };
  const LEATHER = "#3a2314", DARK = "#24160d", CANVAS = "#8f7a58", BRASS = "#c9a24a", MAGENTA = "#b02c70", TEAL = "#1f5566";
  // seat + padded rim, sitting just above the blanket
  add(new RoundedBoxGeometry(0.5, 0.1, 0.6, 2, 0.04), LEATHER, 0, 0.04, 0);
  add(new RoundedBoxGeometry(0.4, 0.04, 0.48, 2, 0.015), TEAL, 0, 0.1, 0);
  // the two saddle posts (a real camel saddle's front/rear horns) + brass knobs
  for (const z of [-0.38, 0.38]) {
    add(new THREE.CylinderGeometry(0.035, 0.055, 0.28, 10), DARK, 0, 0.16, z * 0.85);
    add(new THREE.SphereGeometry(0.05, 10, 8), BRASS, 0, 0.32, z * 0.85);
  }
  // strap over the hump carrying the bags
  add(new THREE.BoxGeometry(0.92, 0.035, 0.1), DARK, 0, -0.01, 0.04);
  for (const sx of [-1, 1]) {
    // canvas saddle bag hanging on each flank (tilted out from the body),
    // leather flap over its top, brass buckle, two magenta tassels below
    add(new RoundedBoxGeometry(0.16, 0.36, 0.44, 2, 0.05), CANVAS, sx * 0.5, -0.36, 0.04, 0, sx * 0.14);
    add(new RoundedBoxGeometry(0.18, 0.14, 0.46, 2, 0.04), LEATHER, sx * 0.52, -0.2, 0.04, 0, sx * 0.14);
    add(new THREE.BoxGeometry(0.04, 0.05, 0.05), BRASS, sx * 0.6, -0.26, 0.04, 0, sx * 0.14);
    for (const z of [-0.12, 0.2]) add(new THREE.ConeGeometry(0.03, 0.14, 6), MAGENTA, sx * 0.44, -0.6, z, Math.PI, 0);
  }
  tackGeo = mergeGeometries(parts)!;
  for (const g of parts) g.dispose();
  tackGeo.computeBoundingSphere();
  tackMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.72, metalness: 0.08 });
  // shared across every camel: never freed by a per-camel dispose walker
  tackGeo.userData.shared = true;
  tackMat.userData.shared = true;
}

/** Start the fetch early (match start). Safe to call repeatedly. */
export function preloadCamelModel(): void {
  void loadGltf(CAMEL_GLB_URL).then(prepare);
}

export class CamelModel {
  /** Container in vehicle-group space. */
  readonly root = new THREE.Group();
  private readonly fit = new THREE.Group();
  private readonly inner: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly acts: THREE.AnimationAction[] = [];
  private readonly w = new Float32Array(2);
  private readonly skinned: THREE.SkinnedMesh[] = [];
  /** Per-camel hide + hair (cloned so the tint is per instance). */
  private readonly coats: THREE.MeshStandardMaterial[] = [];
  private gait = IDLE;
  private readonly jitter = 0.94 + Math.random() * 0.12;

  constructor() {
    const g = template!;
    this.inner = instantiate(g);
    this.fit.rotation.y = fitYaw;
    this.fit.scale.setScalar(fitScale);
    this.inner.position.copy(fitOffset);
    this.fit.add(this.inner);
    this.root.add(this.fit);
    this.root.name = "npCamelModel";
    const clones = new Map<THREE.Material, THREE.MeshStandardMaterial>();
    this.inner.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isSkinnedMesh) return;
      const mat = m.material as THREE.MeshStandardMaterial;
      if (TINTED.has(mat.name)) {
        let c = clones.get(mat);
        if (!c) { c = mat.clone(); c.userData.shared = false; clones.set(mat, c); this.coats.push(c); }
        m.material = c;
      }
      this.skinned.push(m);
    });
    // our tack, placed on the hump at bind pose then re-parented onto the
    // nearest back joint (attach() keeps the placement), so it rides the gait
    buildTack();
    const tack = new THREE.Mesh(tackGeo!, tackMat!);
    tack.name = "npCamelTack";
    tack.castShadow = true;
    tack.receiveShadow = true;
    tack.position.copy(humpTop);
    tack.scale.setScalar(1 / fitScale); // built in metres; inner space is model units
    tack.rotation.y = -fitYaw;          // built facing −Z (vehicle forward)
    this.inner.add(tack);
    this.root.updateMatrixWorld(true);
    let back: THREE.Object3D | null = null, best = Infinity;
    const bp = new THREE.Vector3(), tp = new THREE.Vector3();
    tack.getWorldPosition(tp);
    this.inner.traverse((o) => {
      if (!(o as THREE.Bone).isBone || !/^Back/.test(o.name)) return;
      const d = o.getWorldPosition(bp).distanceToSquared(tp);
      if (d < best) { best = d; back = o; }
    });
    if (back) (back as THREE.Object3D).attach(tack);
    this.mixer = new THREE.AnimationMixer(this.inner);
    for (const name of ["idle", "walk"]) {
      const clip = THREE.AnimationClip.findByName(g.animations, name);
      const a = this.mixer.clipAction(clip);
      a.play();
      a.setEffectiveWeight(name === "idle" ? 1 : 0);
      a.time = Math.random() * clip.duration; // a caravan never moves in lockstep
      this.acts.push(a);
    }
    this.w[IDLE] = 1;
    this.mixer.update(0);
  }

  /** The hide wears the vehicle paint (default camel tan, or the rider's
   *  colour), like the procedural camel's hide did. */
  setCoat(c: THREE.Color): void {
    // lifted a third toward a pale sand: the shared default paint (#b98247)
    // reads as an orange fox on the neutral hide; a rider colour still shows
    for (const m of this.coats) m.color.copy(c).lerp(COAT_LIFT, 0.35);
  }

  /** Once per rendered frame. `speed` = horizontal m/s. */
  update(dt: number, speed: number): void {
    const walk = this.gait === WALK ? speed > WALK_OFF : speed > WALK_ON;
    this.gait = walk ? WALK : IDLE;
    const k = 1 - Math.exp(-dt * 6);
    for (let i = 0; i < this.acts.length; i++) {
      this.w[i] += ((i === this.gait ? 1 : 0) - this.w[i]) * k;
      this.acts[i].setEffectiveWeight(this.w[i]);
    }
    this.acts[WALK].timeScale = Math.min(WALK_MAX_RATE, Math.max(0.6, speed / WALK_NOMINAL)) * this.jitter;
    this.acts[IDLE].timeScale = this.jitter;
    this.mixer.update(dt);
  }

  skinnedMeshes(): readonly THREE.SkinnedMesh[] { return this.skinned; }

  /** Free the per-instance pieces: bone textures, mixer bindings, the tints. */
  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.inner);
    for (const m of this.skinned) m.skeleton.dispose();
    for (const m of this.coats) m.dispose();
    this.root.removeFromParent();
  }
}

interface WarmTarget { readonly scene: THREE.Scene; readonly camera: THREE.Camera; render(): void }

/** Under the match load screen: wait (bounded) for the GLB, render ONE real
 *  frame with a camel in front of the camera (compiles the skinned colour +
 *  shadow programs incl. the alpha-masked hair, uploads textures), then arm.
 *  Caller gates the TV app (realModelsEnabled). */
export async function warmCamelModel(r: WarmTarget, timeoutMs = 3000): Promise<boolean> {
  if (armed && armedFor === r) return true;
  armed = false;
  armedFor = null;
  const g = await Promise.race([
    loadGltf(CAMEL_GLB_URL).then(prepare),
    new Promise<null>((res) => window.setTimeout(() => res(null), timeoutMs)),
  ]);
  if (!g || !template) { console.log("[camel] model not ready at the load-screen deadline — procedural camels this round"); return false; }
  const h = new CamelModel();
  const cam = r.camera;
  const fwd = new THREE.Vector3();
  cam.getWorldDirection(fwd);
  h.root.position.copy(cam.position).addScaledVector(fwd, 6);
  h.root.position.y -= 1.5;
  for (const m of h.skinnedMeshes()) m.frustumCulled = false;
  r.scene.add(h.root);
  h.update(1 / 60, 0);
  try { r.render(); } catch { /* best-effort */ }
  h.dispose();
  armed = true;
  armedFor = r;
  return true;
}
